import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Cloud, HeartPulse, Wallet, Landmark, Bird, HardHat, ClipboardList, PackageMinus,
  Users, Briefcase, Megaphone, Bell, Activity, Radar, Sun, Moon, TrendingUp, MapPin,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePermissions } from "@/lib/permissions";
import { StatCard, PageHero, Section, Badge, Button, EmptyState } from "@/components/premium/UI";
import { Pattern } from "@/components/premium/Patterns";

const fmt = (n, d = 0) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }));
const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30_000); return () => clearInterval(t); }, []);
  return now;
}

function WeatherStrip({ w }) {
  if (!w) return null;
  const c = w?.weather?.current || {};
  return (
    <div className="flex flex-wrap items-center gap-4 text-sm">
      <span className="inline-flex items-center gap-1.5"><Cloud className="w-4 h-4 text-primary" /> {c.temperature_c ?? "—"}°C · {c.description || "—"}</span>
      <span className="inline-flex items-center gap-1.5 text-muted-foreground">{c.humidity_pct != null ? `${c.humidity_pct}% RH · ` : ""}{c.wind_kmh != null ? `${c.wind_kmh} km/h wind` : ""}</span>
      {(w?.weather_alerts || []).length > 0 && <Badge variant="pending">{w.weather_alerts.length} weather alert</Badge>}
    </div>
  );
}

function ActivityFeed() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/activity/recent?limit=8").then(({ data }) => setRows(data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="animate-pulse space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-10 bg-muted/40 rounded-lg" />)}</div>;
  if (!rows.length) return <EmptyState icon={Activity} title="No recent activity yet" description="Activity you're authorised to see will appear here." />;
  return (
    <ul className="space-y-2" data-testid="recent-activity-list">
      {rows.map((r, i) => (
        <li key={r.id || i} className="flex items-center gap-3 rounded-xl px-3 py-2 hover:bg-muted/40 transition-colors">
          <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
            <Activity className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate"><span className="font-medium capitalize">{r.action}</span> · {r.resource} <span className="text-muted-foreground">by {r.user_username || "system"}</span></div>
            <div className="text-[11px] text-muted-foreground">{r.timestamp?.slice(0, 16).replace("T", " ")}</div>
          </div>
          {r.category && <Badge variant="neutral">{r.category}</Badge>}
        </li>
      ))}
    </ul>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { has, roleName, isExecutive } = usePermissions();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const now = useClock();

  useEffect(() => {
    api.get("/dashboard/overview").then(({ data }) => setOverview(data)).catch(() => setOverview({ widgets: {}, widgets_visible: [] })).finally(() => setLoading(false));
  }, []);

  const w = overview?.widgets || {};
  const wx = w.weather;
  const isDay = now.getHours() >= 6 && now.getHours() < 18;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PageHero
        eyebrow={`${overview?.property?.name || "Thom Homestead"} · ${overview?.property?.town || ""}`}
        title={
          <>
            {isDay ? "Good day" : "Good evening"}, <span className="text-primary">{(user?.name || user?.username || "").split(" ")[0]}</span>
          </>
        }
        subtitle={`Welcome to Home360™ · Digital Operations Platform · Signed in as ${roleName || user?.role}${isExecutive ? " (Executive)" : ""}`}
        moduleKind="dashboard"
        actions={
          <>
            <Badge variant="primary">{now.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</Badge>
            <Badge variant="success">{now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</Badge>
            {isDay ? <Badge variant="pending"><Sun className="w-3 h-3" /> Day operations</Badge> : <Badge variant="info"><Moon className="w-3 h-3" /> Night watch</Badge>}
          </>
        }
      >
        {has("widget:weather") && <div className="mt-4" data-testid="widget-weather"><WeatherStrip w={wx} /></div>}
      </PageHero>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-32 rounded-2xl bg-muted/40 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* Health caregiver: today readings, patient */}
          {has("widget:health_today") && (
            <StatCard
              testid="widget-health-today"
              label="Health readings today"
              value={fmt(w.health_today?.today_readings ?? 0)}
              icon={HeartPulse}
              tone="success"
              hint={w.health_today?.patient?.name ? `Patient: ${w.health_today.patient.name}` : "No patient configured"}
            />
          )}
          {has("widget:sales_today") && (
            <StatCard
              testid="widget-sales-today"
              label="Sales today"
              value={money(w.sales_today?.total)}
              icon={Wallet}
              tone="accent"
              hint={`${w.sales_today?.count ?? 0} receipts · ${money(w.sales_today?.outstanding)} outstanding`}
            />
          )}
          {has("widget:cash_reconciliation") && (
            <StatCard
              testid="widget-cash-reconciliation"
              label="Cash reconciliation"
              value={money(w.cash_reconciliation?.on_hand)}
              icon={ClipboardList}
              tone={w.cash_reconciliation?.reconciliation_status === "balanced" ? "success" : "accent"}
              hint={`${money(w.cash_reconciliation?.cash_in_today)} in · ${money(w.cash_reconciliation?.deposited_today)} deposited`}
            />
          )}
          {has("widget:poultry_ops") && (
            <StatCard
              testid="widget-poultry-ops"
              label="Current flock"
              value={fmt(w.poultry_ops?.current_birds)}
              icon={Bird}
              tone="accent"
              hint={`${w.poultry_ops?.active_batches ?? 0} active batches · ${w.poultry_ops?.mortality_pct ?? 0}% mortality`}
            />
          )}
          {has("widget:construction_progress") && w.construction_progress && (
            <StatCard
              testid="widget-construction-progress"
              label={w.construction_progress.project || "Construction"}
              value={`${fmt(w.construction_progress.completion_pct)}%`}
              icon={HardHat}
              tone="accent"
              hint={`Phase: ${w.construction_progress.phase || "—"} · target ${w.construction_progress.target_completion || "—"}`}
            />
          )}
          {has("widget:pending_approvals") && (
            <StatCard
              testid="widget-pending-approvals"
              label="Pending approvals"
              value={fmt(w.pending_approvals?.count)}
              icon={ClipboardList}
              tone={w.pending_approvals?.count > 0 ? "accent" : "muted"}
              hint="Daily project submissions"
            />
          )}
          {has("widget:inventory_low_stock") && (
            <StatCard
              testid="widget-inventory-low"
              label="Low-stock alerts"
              value={fmt(w.inventory_low_stock?.low_stock_count)}
              icon={PackageMinus}
              tone={w.inventory_low_stock?.low_stock_count > 0 ? "danger" : "success"}
              hint={`${fmt(w.inventory_low_stock?.total_items)} items tracked`}
            />
          )}
          {has("widget:cash_position") && (
            <StatCard
              testid="widget-cash-position"
              label="Cash position"
              value={money(w.cash_position?.cash_position)}
              icon={Landmark}
              tone="primary"
              hint={`${money(w.cash_position?.income)} in · ${money(w.cash_position?.expenses)} out`}
            />
          )}
          {has("widget:executive_summary") && (
            <StatCard
              testid="widget-executive-summary"
              label="Net position"
              value={money(w.executive_summary?.net_cash)}
              icon={TrendingUp}
              tone="primary"
              hint={w.executive_summary?.active_project ? `Live project: ${w.executive_summary.active_project}` : "No active project"}
            />
          )}
          {has("widget:hr_stats") && (
            <StatCard
              testid="widget-hr-stats"
              label="Active employees"
              value={fmt(w.hr_stats?.active_employees)}
              icon={Briefcase}
              tone="info"
              hint={`${fmt(w.hr_stats?.on_leave)} on leave today`}
            />
          )}
          {has("widget:marketing_stats") && (
            <StatCard
              testid="widget-marketing-stats"
              label="Active campaigns"
              value={fmt(w.marketing_stats?.active_campaigns)}
              icon={Megaphone}
              tone="info"
              hint={`${fmt(w.marketing_stats?.scheduled_campaigns)} scheduled`}
            />
          )}
          {has("widget:alerts") && (
            <StatCard
              testid="widget-alerts"
              label="Unread notifications"
              value={fmt(w.alerts?.unread)}
              icon={Bell}
              tone={w.alerts?.critical > 0 ? "danger" : "muted"}
              hint={`${fmt(w.alerts?.critical)} critical`}
            />
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {has("widget:recent_activity") && (
          <Section
            title="Recent activity"
            description="Filtered to only what your role is authorised to see."
            className="lg:col-span-2 relative overflow-hidden"
            actions={<Badge variant="primary">Live</Badge>}
          >
            <Pattern kind="dashboard" />
            <div className="relative" data-testid="recent-activity-feed"><ActivityFeed /></div>
          </Section>
        )}

        {has("widget:situation_room") && w.situation_room && (
          <Section
            title="Situation room"
            description="Executive operational health score"
            actions={<Link to="/situation"><Button variant="ghost" size="sm">Open</Button></Link>}
          >
            <div className="text-5xl font-heading font-bold tracking-tight text-primary">
              {fmt(w.situation_room.score ?? w.situation_room?.overall_score)}
            </div>
            <p className="subtle mt-1">Composite score across finance, operations & health.</p>
            <div className="mt-4 h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-gradient-to-r from-primary to-accent" style={{ width: `${Math.max(0, Math.min(100, w.situation_room?.score ?? 0))}%` }} />
            </div>
          </Section>
        )}

        {!has("widget:recent_activity") && !has("widget:situation_room") && (
          <Section title="Quick links" className="lg:col-span-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {has("module:health") && <Link to="/health" className="rounded-xl border border-border p-3 hover:bg-muted/40 flex items-center gap-2"><HeartPulse className="w-4 h-4 text-emerald-500" /> Health360</Link>}
              {has("module:sales") && <Link to="/sales" className="rounded-xl border border-border p-3 hover:bg-muted/40 flex items-center gap-2"><Wallet className="w-4 h-4 text-amber-500" /> Sales & Cash</Link>}
              {has("module:poultry") && <Link to="/poultry" className="rounded-xl border border-border p-3 hover:bg-muted/40 flex items-center gap-2"><Bird className="w-4 h-4 text-amber-500" /> Poultry360</Link>}
              {has("module:projects") && <Link to="/projects" className="rounded-xl border border-border p-3 hover:bg-muted/40 flex items-center gap-2"><HardHat className="w-4 h-4 text-orange-500" /> Projects360</Link>}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}
